import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MessageCircleMore, User } from "lucide-react";
import WishForm from "./WishForm";
import { useParams } from "react-router-dom";
import { listenToWishes } from "../../services/cardService";
import { getGuestWishesApi } from "../../api/cardApi";
import { getTranslations } from "../../utils/CardTranslations";



const WishSection = ({ data, previewWishes, disableLive, source = 'firebase', uniqueCode }) => {

    const buttonStyle = {
        marginTop: 3,
        textTransform: "none",
        border: "none",
        color: data.themeColour,
        backgroundColor: data?.buttonColour || 'white',
        borderRadius: "50px",
        gap: 1,
        fontSize: "12px",
        px: "15px",
        py: "8px",
        boxShadow: '0 8px 16px 0 rgba(0,0,0,0.15)',
        "&:hover": {
            transition: "0.4s",
        },
    };

    const [wishes, setWishes] = useState([]);
    const [showGuestWish, setShowGuestWish] = useState(false)
    const [closing, setClosing] = useState(false);
    const { cardId } = useParams();
    const designCode = data?.designCode;
    const t = getTranslations(data?.language);

    const openWishForm = () => {
        setShowGuestWish(true);
    };

    const closeWishForm = () => {
        setClosing(true);

        setTimeout(() => {
            setShowGuestWish(false);
            setClosing(false);
        }, 150); // MUST match CSS duration
    };

    // Normalize API wish shape to match Firebase shape
    const normalizeWishes = (apiWishes) =>
        apiWishes.map((w) => ({
            id: w.id,
            name: w.senderName,
            guestWish: w.message,
            createdAt: w.createdAt ? { toDate: () => new Date(w.createdAt) } : null,
        }));

    // Re-usable fetch — called on mount AND after a new wish is posted
    const fetchWishes = () => {
        if (!uniqueCode) return;
        getGuestWishesApi(uniqueCode)
            .then((apiWishes) => setWishes(normalizeWishes(apiWishes)))
            .catch((err) => console.error('Failed to fetch wishes:', err));
    };

    useEffect(() => {

        if (disableLive === true) {
            setWishes(previewWishes ?? []);
            return;
        }

        if (source === 'api') {
            fetchWishes();

            // Listen for any component (like RSVPForm) saying a wish was successfully posted
            window.addEventListener('wish-posted', fetchWishes);

            return () => {
                window.removeEventListener('wish-posted', fetchWishes);
            }
        } else {
            // Old Firebase cards — real-time listener
            if (!cardId) return;
            const unsubscribe = listenToWishes(cardId, setWishes);
            return () => unsubscribe();
        }
    }, [source, cardId, uniqueCode, disableLive, previewWishes]);


    return (
        <Box
            sx={{
                textAlign: "center",
                pt: 1,
                pb: 4,
            }}
        >
            <Typography
                sx={{ color: data.themeColour, fontSize: "15px", fontWeight: "bold" }}
            >
                {t.wishTitle}
            </Typography>

            {/* Scroll container - SCROLLING but scrollbar invisible */}
            <Box
                sx={{
                    textAlign: "start",
                    mt: 2,
                    justifyItems: "center",
                    width: "100%",
                    textAlign: "center",
                    maxHeight: "350px",
                    overflowY: "scroll",
                    py: 1,
                    // Hide scrollbar
                    "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
                    scrollbarWidth: "none", // Firefox
                    msOverflowStyle: "none", // Edge/IE
                }}
            >
                {wishes.map((wish) => (
                    <Box
                        key={wish.id}
                        sx={{
                            display: "flex",
                            justifyContent: "center",   // <-- FIX FOR iPHONE
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                backgroundColor: data?.buttonColour || 'white',
                                //border: `solid 2px ${data.themeColour}`,
                                boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.12)",
                                borderRadius: "20px",
                                width: "70%",
                                py: "18px",
                                px: "18px",
                                mb: 2,
                                gap: 2,
                            }}
                        >
                            {/* Profile Icon */}
                            <Box
                                sx={{
                                    backgroundColor: alpha(data.themeColour, 0.13),
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,

                                }}
                            >
                                <User size={22} color={data.themeColour} />
                            </Box>

                            {/* Text Section */}
                            <Box sx={{ textAlign: "left" }}>
                                {/* Name */}
                                <Typography sx={{ mb: 0.2, fontSize: 14, fontWeight: "600", color: data.themeColour, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "180px" }}>
                                    {wish.name || t.guestFallback}
                                </Typography>

                                {/* Message */}
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: data.textWishColour,
                                        lineHeight: 1.3,
                                        mb: 0.2,
                                    }}
                                >
                                    {wish.guestWish || "-"}
                                </Typography>

                                {/* Date */}
                                {wish.createdAt?.toDate && (
                                    <Typography sx={{ fontSize: 11, opacity: 0.7, color: data.textWishColour }}>
                                        {wish.createdAt.toDate().toLocaleDateString("en-GB", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                        })}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                    </Box>

                ))}

                {wishes.length === 0 && (
                    <Typography sx={{ fontSize: 12, opacity: 0.6, color: designCode === "floral-007" ? "white" : "text.secondary" }}>
                        {t.noWishes}
                    </Typography>
                )}

            </Box>

            {/* Button Hantar Ucapan & Display Form*/}
            <Button
                variant="outlined"
                sx={buttonStyle}
                onClick={openWishForm}
            >
                <MessageCircleMore size={16} />
                {t.sendWish}
            </Button>

            {showGuestWish && (
                <>
                    <div className="rsvp-overlay" onClick={closeWishForm} />

                    <div className={`rsvp-popup ${closing ? "popup-close" : "popup-open"}`}>
                        <WishForm
                            data={data}
                            source={source}
                            uniqueCode={uniqueCode}
                            onWishPosted={source === 'api' ? fetchWishes : undefined}
                            onClose={closeWishForm}
                        />
                    </div>
                </>
            )}
        </Box >
    )
};

export default WishSection